import { useEffect, useMemo } from 'react';
import { type StoreApi, type UseBoundStore, create } from 'zustand';

import type {
  ApiEventId,
  ApiInternshipId,
  ApiJobId,
  ApiOrgId,
  ApiVolopId,
} from 'api/types/ApiTypedId';
import {
  useActionsWhileLoggedOutProcessedAt,
  useLoggedIn,
} from 'contexts/UserEnvironmentContext';
import { useSaveListing } from 'modules/listing/hooks/useSaveListing';
import { useSaveOrgAfterListing } from 'modules/listing/hooks/useSaveOrgAfterListing';
import type { SearchAlgoliaHit } from 'modules/search/algolia/types/SearchAlgoliaHit';
import { userDashboardApiClientFetchListingExtras } from 'modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';

type ApiListingId =
  | ApiJobId
  | ApiInternshipId
  | ApiVolopId
  | ApiEventId
  | ApiOrgId;

export type SearchSavedListingsStoreState = Partial<
  Record<ApiListingId, boolean>
>;

export type SearchSavedListingsStore = UseBoundStore<
  StoreApi<SearchSavedListingsStoreState>
>;

export function createSearchSavedListingsStore(
  initialState: SearchSavedListingsStoreState,
) {
  return create<SearchSavedListingsStoreState>(() => initialState);
}

let lastActionsWhileLoggedOutProcessedAt: number | null;

export async function pullSearchSavedListingsFromHits({
  hits,
  searchSavedListingsStore,
  actionsWhileLoggedOutProcessedAt,
}: {
  hits: SearchAlgoliaHit[] | undefined;
  searchSavedListingsStore: SearchSavedListingsStore;
  actionsWhileLoggedOutProcessedAt: number | null; // Data needs to be re-fetched after user logs in, and we process data in temporary storage/location state
}) {
  if (!hits) return;

  const listingIds = new Set(
    hits
      .filter((hit) => hit.type !== 'IMPORTED')
      .flatMap((hit) =>
        [hit.objectID, 'orgID' in hit && hit.orgID].filter(Boolean),
      ),
  );

  const processedSinceLastPull =
    actionsWhileLoggedOutProcessedAt !== lastActionsWhileLoggedOutProcessedAt;
  const existingIds = new Set(
    processedSinceLastPull
      ? []
      : Object.keys(searchSavedListingsStore.getState()),
  );
  const missingListingIds = listingIds.difference(existingIds);
  if (missingListingIds.size === 0) return;

  const listingExtras = await userDashboardApiClientFetchListingExtras(
    Array.from(missingListingIds),
  );

  searchSavedListingsStore.setState((currentState) => {
    const newState = { ...currentState };

    missingListingIds.forEach((listingId) => {
      newState[listingId] = listingExtras[listingId]?.saved ?? false;
    });

    return newState;
  });

  lastActionsWhileLoggedOutProcessedAt = actionsWhileLoggedOutProcessedAt;
}

export function useSearchSavedListingsStore() {
  const searchSavedListingsStore = useMemo(
    () => createSearchSavedListingsStore({}),
    [],
  );

  return { searchSavedListingsStore };
}

export function useSearchSavedListingsSyncAlgoliaHits({
  searchSavedListingsStore,
  hits,
}: {
  searchSavedListingsStore: SearchSavedListingsStore;
  hits: SearchAlgoliaHit[] | undefined;
}) {
  const { loggedIn } = useLoggedIn();
  const { actionsWhileLoggedOutProcessedAt } =
    useActionsWhileLoggedOutProcessedAt();

  useEffect(() => {
    if (!loggedIn) return;
    void pullSearchSavedListingsFromHits({
      hits,
      searchSavedListingsStore,
      actionsWhileLoggedOutProcessedAt,
    });
  }, [
    loggedIn,
    hits,
    searchSavedListingsStore,
    actionsWhileLoggedOutProcessedAt,
  ]);

  return { searchSavedListingsStore };
}

export function useSearchSavedListingsHit({
  hit,
  searchSavedListingsStore,
}: {
  hit: SearchAlgoliaHit;
  searchSavedListingsStore: SearchSavedListingsStore;
}) {
  const listingId = hit.objectID;

  const org =
    hit.type !== 'ORG' && hit.orgID && hit.orgName && hit.orgType
      ? { id: hit.orgID, name: hit.orgName, orgType: hit.orgType }
      : null;
  const orgId = org?.id ?? null;
  const orgName = org?.name ?? null;
  const orgType = org?.orgType ?? null;

  const {
    orgSaved,
    saveOrg,
    showSaveOrgTooltip,
    shouldShowSaveOrgTooltip,
    dismissSaveOrgTooltip,
  } = useSaveOrgAfterListing({
    orgId,
    orgName,
    orgType,
    orgInitiallySaved: org
      ? Boolean(searchSavedListingsStore.getState()[org.id])
      : false,
    searchSavedListingsStore: searchSavedListingsStore,
  });

  const { saved, save, unsave } = useSaveListing({
    listingType: hit.type,
    listingId,
    listingName: hit.name,
    initiallySaved: Boolean(searchSavedListingsStore.getState()[hit.objectID]),
    onSaved: showSaveOrgTooltip,
    onUnsaved: dismissSaveOrgTooltip,
    orgId,
    orgType,
    trackClickEvent: true,
    searchSavedListingsStore: searchSavedListingsStore,
  });

  return {
    // Listing
    listingSaved: saved,
    saveListing: save,
    unsaveListing: unsave,

    // Org
    orgSaved,
    saveOrg,
    dismissSaveOrgTooltip,
    shouldShowSaveOrgTooltip,
  };
}
